import React, {useCallback, useEffect, useState} from 'react'
import {useRouter} from 'next/router'

import styled from 'styled-components'
import useEmblaCarousel from 'embla-carousel-react'
import classNames from 'classnames'
import {media} from 'styled-bootstrap-grid'
import {useMedia} from 'react-use'

import {
  NextButton,
  PrevButton,
  usePrevNextButtons,
} from './EmblaCarouselArrowButtons'
import {DotButton, useDotButton} from './EmblaCarouselDotButton'
import {ThumbButton, useThumbButton} from './EmblaCarouselThumbButton'
import {styledTheme} from '@festi/common/themes'

const Root = styled.div`
  position: relative;
  display: grid;
  grid-template-rows: auto 34px;
  align-items: center;
  gap: 8px;
  width: 100%;
  height: 100%;
  max-height: 100dvh;
  overflow: hidden;

  &.hasMobileThumbs {
    grid-template-rows: auto 64px;
    gap: 16px;
  }

  ${media.md`
    grid-template-rows: auto 80px;
    gap: 40px;

    &.singleImage {
      grid-template-rows: auto 0;
      gap: 0;
    }
  `}
`

const ArrowBtnContainer = styled.div`
  position: absolute;
  display: none;
  justify-content: space-between;
  top: calc(50% - 24px);
  right: 0;
  left: 0;
  z-index: 1;

  ${media.md`
    &.showArrowButtons {
      display: flex;
    }
  `}
`

const Carousel = styled.div`
  overflow: hidden;
  height: 100%;
  width: 100%;
`

const EmblaContainer = styled.div`
  display: flex;
  height: 100%;
  touch-action: pan-y pinch-zoom;
`

const MainSlide = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  flex: 0 0 100%;
  padding-inline: 16px;
  padding-top: 28px;
  cursor: pointer;

  &:focus {
    outline: none !important;
  }

  &:focus-visible {
    border: 2px solid ${styledTheme.palette.lightBlue};
    border-radius: 12px;
  }

  &.isFullScreen {
    padding: 0;
    cursor: default;
  }

  .demoupUI-img {
    height: 176px !important;

    ${media.md`
      height: 225px !important;
    `}
  }

  ${media.md`
    padding-top: unset;
    padding-inline: 60px;

    &.isFullScreen {
      padding-inline: 60px;
    }
  `}
`

const DotButtonsWrapper = styled.div`
  display: none;
  gap: 5px;
  justify-content: center;
  align-content: center;
  height: 6px;

  &.showInMobile {
    display: flex;
  }

  ${media.md`
    &.showInMobile {
      display: none;
    }
  `}
`

const ThumbButtonsWrapper = styled.div`
  overflow: hidden;
  height: 64px;
  width: 100%;

  &.hideInMobile {
    display: none;
  }

  ${media.md`
    height: 80px;

    &.hideInMobile {
      display: block;
    }
  `}
`

const ThumbsContainer = styled.div`
  display: flex;
  gap: 8px;
  height: 64px;
  align-items: center;
  align-self: end;

  ${media.md`
    height: 80px;
  `}
`

interface CarouselProps {
  slides: JSX.Element[]
  thumbSlides: JSX.Element[]
  showArrowButtons?: boolean
  showDotButtons?: boolean
  fullScreenStartSlide?: number | undefined
  setFullScreenStartSlide?: (index: number | undefined) => void
  isFullScreen?: boolean
  isZoomed?: boolean
  setPreviousRoute?: (route: string) => void
  removePreviousRoute?: () => void
}

export default function EmblaCarousel({
  slides,
  thumbSlides,
  showArrowButtons = false,
  showDotButtons = false,
  fullScreenStartSlide,
  setFullScreenStartSlide,
  isFullScreen = false,
  isZoomed = false,
  setPreviousRoute,
  removePreviousRoute,
}: CarouselProps) {
  const router = useRouter()

  const isMobile = useMedia(
    `(max-width: ${styledTheme.screenSize.sizeMD})`,
    false,
  )

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    inViewThreshold: 0.25,
  })
  const [emblaThumbsRef, emblaThumbsApi] = useEmblaCarousel({
    containScroll: 'keepSnaps',
    dragFree: true,
    inViewThreshold: 0.25,
  })
  const [selectedIndex, setSelectedIndex] = useState(0)
  const {scrollSnaps, onDotButtonClick} = useDotButton(
    emblaApi,
    setSelectedIndex,
  )
  const {onThumbButtonClick} = useThumbButton(
    emblaApi,
    emblaThumbsApi,
    setSelectedIndex,
  )

  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  } = usePrevNextButtons(emblaApi)

  const handleSlideClick = useCallback(
    (index: number) => {
      if (!isFullScreen) {
        // This lets us know if full screen gallery was opened from product page and not from a url link
        const prevRoute = router.asPath
        setPreviousRoute(prevRoute)

        setFullScreenStartSlide && setFullScreenStartSlide(index)

        const path = {
          pathname: router.pathname,
          query: {...router.query, fullScreenGallery: true},
        }
        router.push(path, undefined, {
          shallow: true,
        })
      }
    },
    [isFullScreen, router, setFullScreenStartSlide, setPreviousRoute],
  )

  const hasMultipleSlides = slides.length > 1

  const onKeyDownMainSlide = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !isFullScreen) {
        handleSlideClick(selectedIndex)
      }
    },
    [handleSlideClick, isFullScreen, selectedIndex],
  )

  const onKeyDownThumbsOrDots = useCallback(
    (
      e: React.KeyboardEvent,
      index: number,
      onBtnClick: (index: number) => void,
    ) => {
      if (e.key === 'Tab' && e.shiftKey && emblaApi.canScrollPrev()) {
        onBtnClick(index - 1)
      } else if (e.key === 'Tab' && emblaApi.canScrollNext()) {
        onBtnClick(index + 1)
      }
    },
    [emblaApi],
  )

  useEffect(() => {
    if (!emblaApi) return
    if (fullScreenStartSlide !== undefined) {
      emblaApi.scrollTo(fullScreenStartSlide, true)
      setFullScreenStartSlide(undefined)
    }
  }, [emblaApi, fullScreenStartSlide, setFullScreenStartSlide])

  useEffect(() => {
    if (!emblaApi) return
    if (isZoomed) {
      emblaApi.reInit({watchDrag: false})
    } else if (!isZoomed) {
      emblaApi.reInit({watchDrag: true})
    }
  }, [emblaApi, isZoomed])

  useEffect(() => {
    const handlePopState = () => {
      removePreviousRoute && removePreviousRoute()
    }

    window.addEventListener('popstate', handlePopState)

    return () => {
      window.removeEventListener('popstate', handlePopState)
    }
  }, [removePreviousRoute, router])

  return (
    <Root
      className={classNames({
        hasMobileThumbs: isMobile && !showDotButtons,
        singleImage: !hasMultipleSlides,
      })}
    >
      <ArrowBtnContainer className={classNames({showArrowButtons})}>
        <PrevButton onClick={onPrevButtonClick} disabled={prevBtnDisabled} />
        <NextButton onClick={onNextButtonClick} disabled={nextBtnDisabled} />
      </ArrowBtnContainer>

      <Carousel className="embla" ref={emblaRef}>
        <EmblaContainer className="embla__container">
          {slides.map((slide, index) => (
            <MainSlide
              className={classNames('embla__slide', {
                isFullScreen,
              })}
              key={index}
              onClick={() => handleSlideClick(index)}
              role="button"
              tabIndex={!isFullScreen && selectedIndex === index ? 0 : -1}
              onKeyDown={onKeyDownMainSlide}
              aria-label="Myndagallerí"
            >
              {slide}
            </MainSlide>
          ))}
        </EmblaContainer>
      </Carousel>

      {hasMultipleSlides && (
        <>
          <DotButtonsWrapper
            className={classNames({showInMobile: showDotButtons})}
          >
            {scrollSnaps.map((_, index) => (
              <DotButton
                key={index}
                onClick={() => onDotButtonClick(index)}
                className={classNames({selected: index === selectedIndex})}
                onFocus={(e) => {
                  if (index !== selectedIndex) {
                    e.preventDefault()
                    onDotButtonClick(index)
                  }
                }}
                onKeyDown={(e) => {
                  onKeyDownThumbsOrDots(e, index, onDotButtonClick)
                }}
                aria-label="Dot button"
              />
            ))}
          </DotButtonsWrapper>
          <ThumbButtonsWrapper
            className={classNames('embla-thumbs', {
              hideInMobile: showDotButtons,
            })}
            ref={emblaThumbsRef}
          >
            <ThumbsContainer className="embla-thumbs__container">
              {thumbSlides?.map((slide, index) => (
                <ThumbButton
                  key={index}
                  onClick={() => onThumbButtonClick(index)}
                  selected={index === selectedIndex}
                  onFocus={(e) => {
                    if (index !== selectedIndex) {
                      e.preventDefault()
                      onThumbButtonClick(index)
                    }
                  }}
                  onKeyDown={(e) => {
                    onKeyDownThumbsOrDots(e, index, onThumbButtonClick)
                  }}
                  aria-label="Thumb button"
                >
                  {slide}
                </ThumbButton>
              ))}
            </ThumbsContainer>
          </ThumbButtonsWrapper>
        </>
      )}
    </Root>
  )
}
